import type {
  MediaAssetDto,
  ProductVariantSearchResultDto,
} from '@/apis/backend/generated/models';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '@/hooks/use-formatters';
import { DashboardNotifications } from '@/components/dashboard';
import { useOrganization } from '@/context/organization-context';
import { usePromptSuggestions } from '@/hooks/usePromptSuggestions';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { FEATURE_FLAGS, useFeatureFlag } from '@/hooks/useFeatureFlag';
import { BrandProspectPanel } from '@/components/billing/BrandProspectPanel';
import { MediaPickerModal } from '@/components/email-generation/MediaPickerModal';
import { FigmaPluginReadyDialog } from '@/components/figma/FigmaPluginReadyDialog';
import { WgDashboardView } from '@/sections/white-glove/dashboard/wg-dashboard-view';
import { ProductPickerModal } from '@/components/email-generation/ProductPickerModal';
import { UpgradeModal, type UpgradeModalType } from '@/components/billing/UpgradeModal';
import { useBillingControllerGetQuota } from '@/apis/backend/generated/billing/billing';
import { useAuthControllerGetOnboardingStatus } from '@/apis/backend/generated/auth/auth';
import { useHomepageControllerGetHomepageData } from '@/apis/backend/generated/homepage/homepage';
import { useTemplateSuggestionsControllerGetWeeklySuggestions } from '@/apis/backend/generated/templates/templates';
import {
  useCanAuthorEmails,
  useWhiteGloveAccess,
  useBrandRelationship,
} from '@/sections/white-glove/use-white-glove-access';
import { useCampaignAnalyticsControllerGetCampaignOverview } from '@/apis/backend/generated/analytics-campaigns/analytics-campaigns';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { alpha, useTheme } from '@mui/material/styles';
import CardActionArea from '@mui/material/CardActionArea';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Add as AddIcon,
  Send as SendIcon,
  Close as CloseIcon,
  EditNote as EditNoteIcon,
  AttachFile as AttachIcon,
  ShoppingBag as ProductIcon,
  TrendingUp as TrendingUpIcon,
  AutoAwesome as AutoAwesomeIcon,
  MailOutline as MailOutlineIcon,
  ChevronLeft as ChevronLeftIcon,
  ArrowUpward as ArrowUpwardIcon,
  ChevronRight as ChevronRightIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
} from '@mui/icons-material';

// generation is handled on the create page; dashboard just navigates with params
import { paths } from 'src/routes/paths';

import { useAuth } from 'src/lib/store/auth-store';
import { DashboardContent } from 'src/layouts/dashboard';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/')({
  component: BrandHome,
});

// ----------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { label: string; color: string; bgcolor: string }> = {
  draft: { label: 'Draft', color: 'text.secondary', bgcolor: 'grey.200' },
  ready: { label: 'Ready', color: 'success.dark', bgcolor: 'success.lighter' },
  exported: { label: 'Sent', color: 'info.dark', bgcolor: 'info.lighter' },
  archived: { label: 'Archived', color: 'text.disabled', bgcolor: 'grey.100' },
};

// ----------------------------------------------------------------------

interface LearningBrandCardProps {
  onCompleteBrand?: () => void;
}

function LearningBrandCard({ onCompleteBrand }: LearningBrandCardProps) {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();

  const primary = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark;

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '20px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: alpha(primary, 0.18),
        background: `linear-gradient(180deg, ${alpha(primary, 0.06)} 0%, ${alpha(primary, 0.015)} 60%, transparent 100%)`,
        boxShadow: `0 1px 0 0 ${alpha(primary, 0.06)} inset`,
        '@keyframes lb-aurora': {
          '0%': { transform: 'translate3d(-10%, -10%, 0) rotate(0deg)' },
          '50%': { transform: 'translate3d(10%, 6%, 0) rotate(180deg)' },
          '100%': { transform: 'translate3d(-10%, -10%, 0) rotate(360deg)' },
        },
        '@keyframes lb-shimmer': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' },
        },
        '@keyframes lb-pulse': {
          '0%, 100%': { opacity: 0.55, transform: 'scale(1)' },
          '50%': { opacity: 1, transform: 'scale(1.06)' },
        },
        '@keyframes lb-orbit': {
          '0%': { transform: 'rotate(0deg) translateX(38px) rotate(0deg)' },
          '100%': { transform: 'rotate(360deg) translateX(38px) rotate(-360deg)' },
        },
        '@keyframes lb-orbit-rev': {
          '0%': { transform: 'rotate(0deg) translateX(58px) rotate(0deg)' },
          '100%': { transform: 'rotate(-360deg) translateX(58px) rotate(360deg)' },
        },
      }}
    >
      {/* Aurora background */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: -80,
          background: `radial-gradient(closest-side, ${alpha(primary, 0.22)}, transparent 70%), radial-gradient(closest-side, ${alpha(primaryDark, 0.18)}, transparent 70%)`,
          backgroundPosition: '20% 20%, 80% 60%',
          backgroundSize: '60% 60%, 60% 60%',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(40px)',
          opacity: 0.7,
          animation: 'lb-aurora 18s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', px: 3, pt: 4, pb: 3 }}>
        {/* Hero icon with orbiting dots */}
        <Box
          sx={{
            position: 'relative',
            width: 132,
            height: 132,
            mx: 'auto',
            mb: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Concentric rings */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: '1px dashed',
              borderColor: alpha(primary, 0.25),
              animation: 'lb-pulse 4s ease-in-out infinite',
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              right: 16,
              bottom: 16,
              borderRadius: '50%',
              border: '1px solid',
              borderColor: alpha(primary, 0.18),
            }}
          />
          {/* Orbiting dots */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 8,
              height: 8,
              ml: '-4px',
              mt: '-4px',
              borderRadius: '50%',
              bgcolor: primary,
              boxShadow: `0 0 12px ${alpha(primary, 0.7)}`,
              animation: 'lb-orbit 6s linear infinite',
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 5,
              height: 5,
              ml: '-2.5px',
              mt: '-2.5px',
              borderRadius: '50%',
              bgcolor: primaryDark,
              opacity: 0.6,
              animation: 'lb-orbit-rev 9s linear infinite',
            }}
          />
          {/* Center sparkle */}
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
              boxShadow: `0 8px 28px ${alpha(primary, 0.45)}`,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 28, color: 'common.white' }} />
          </Box>
        </Box>

        {/* Headline */}
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            color: 'text.primary',
            textAlign: 'center',
            letterSpacing: '-0.01em',
            mb: 0.75,
          }}
        >
          {t('home.learning.title')}
        </Typography>
        <Typography
          sx={{
            fontSize: 13.5,
            color: 'text.secondary',
            textAlign: 'center',
            maxWidth: 360,
            mx: 'auto',
            lineHeight: 1.55,
            mb: 2.5,
          }}
        >
          {t('home.learning.body')}
        </Typography>


        {/* Ghost preview tiles */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            mb: 2.5,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                position: 'relative',
                aspectRatio: '3 / 4',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: alpha(primary, 0.12),
                bgcolor: alpha(primary, 0.04),
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  background: `linear-gradient(180deg, ${alpha(primary, 0.16)} 0%, transparent 50%, ${alpha(primary, 0.08)} 100%)`,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  background: `linear-gradient(90deg, transparent 0%, ${alpha('#fff', 0.5)} 50%, transparent 100%)`,
                  animation: 'lb-shimmer 2.8s linear infinite',
                  animationDelay: `${i * 0.4}s`,
                },
              }}
            >
              {/* Faux content lines */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '14%',
                  right: '14%',
                  top: '22%',
                  height: 4,
                  borderRadius: 2,
                  bgcolor: alpha(primary, 0.22),
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: '14%',
                  width: '50%',
                  top: '32%',
                  height: 4,
                  borderRadius: 2,
                  bgcolor: alpha(primary, 0.16),
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: '14%',
                  right: '14%',
                  bottom: '22%',
                  height: 18,
                  borderRadius: 4,
                  bgcolor: alpha(primary, 0.18),
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Optional CTA: complete brand profile to speed things up */}
        {onCompleteBrand && (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              size="small"
              variant="text"
              onClick={onCompleteBrand}
              endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 600,
                color: primary,
                '&:hover': { bgcolor: alpha(primary, 0.06) },
              }}
            >
              {t('home.learning.cta')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

/**
 * The brand's home, which is not the same page for every brand.
 *
 * A managed brand is not here to write an email -- they are paying us to write
 * them -- so leading with a prompt box asks them for the one thing they bought
 * their way out of. They get the service's own landing page instead: what is
 * waiting on them, what is in flight, what sends next, and what it earned.
 *
 * A brand at the other end has the opposite problem. Nothing has been chosen
 * for it and the builder is off, so the dashboard below reports on emails that
 * cannot exist yet and offers no way to make any: three zeroes and an empty
 * list. Anyone who skipped the fork at signup, was added afterwards, or arrived
 * some other way lands there, and the page reads as broken rather than as
 * unstarted. They get the fork instead, which is the question the page was
 * failing to ask.
 */
function BrandHome() {
  const { enabled: managed, isLoading } = useWhiteGloveAccess();
  const { isProspect, isLoading: relationshipLoading } = useBrandRelationship();
  const canAuthor = useCanAuthorEmails();
  // Held here rather than in the panel below, because taking a Figma branch is
  // exactly what stops this brand being a prospect: the answer that opens this
  // dialog is the same one that swaps the panel out from under it.
  const [pluginReady, setPluginReady] = useState<'trial' | 'purchased' | null>(null);

  // Wait rather than guess. The pages have different centrepieces, and flashing
  // the wrong one is worse than a moment of nothing.
  if (isLoading || relationshipLoading) {
    return (
      <DashboardContent maxWidth="xl" sx={{ pt: { xs: 2, sm: 4 } }}>
        <Stack alignItems="center" sx={{ py: 10 }}>
          <CircularProgress size={28} />
        </Stack>
      </DashboardContent>
    );
  }

  if (managed) return <WgDashboardView />;

  // The fork only where there is genuinely nothing else. A brand that can still
  // author its own emails has a working page, and taking it over with a sales
  // question would interrupt the product it already has -- that brand is offered
  // the two free designs by the nudge on the dashboard instead. Where it does
  // show there is no way past it, because the page behind it is the empty
  // dashboard it replaced.
  const page =
    isProspect && !canAuthor ? <ProspectHome onFigmaLanded={setPluginReady} /> : <DashboardPage />;

  return (
    <>
      {page}
      <FigmaPluginReadyDialog
        open={pluginReady !== null}
        kind={pluginReady}
        onClose={() => setPluginReady(null)}
      />
    </>
  );
}

/**
 * The fork, asked on the dashboard.
 *
 * The panel is the onboarding fork itself rather than a second copy of it: the
 * question does not change with where it is asked, and one of the two would
 * have drifted.
 */
function ProspectHome({ onFigmaLanded }: { onFigmaLanded: (kind: 'trial' | 'purchased') => void }) {
  const { activeOrganization, activeClient } = useOrganization();
  // The org's own record of who signed up. Agencies are the only accounts
  // offered the Figma branch, and this is the only place the web can read it.
  const { data: onboarding } = useAuthControllerGetOnboardingStatus({
    query: { staleTime: 300_000, retry: false },
  });

  const orgId = activeOrganization?.id;
  const clientId = activeClient?.id;
  if (!orgId || !clientId) return <DashboardPage />;

  return (
    <DashboardContent maxWidth="lg" sx={{ pt: { xs: 2, sm: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 28, fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          Welcome to nexmail
        </Typography>
        <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>
          {activeClient?.name
            ? `Let's get the first emails made for ${activeClient.name}.`
            : "Let's get your first emails made."}
        </Typography>
      </Box>

      <BrandProspectPanel
        orgId={orgId}
        clientId={clientId}
        isAgency={onboarding?.accountType === 'agency'}
        onFigmaLanded={onFigmaLanded}
      />
    </DashboardContent>
  );
}

function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const { formatRelative } = useFormatters();
  const theme = useTheme();
  const navigate = useNavigate();
  const { activeClient, activeOrganization } = useOrganization();
  const { dbUser } = useAuth();
  const clientId = activeClient?.id;
  const orgId = activeOrganization?.id;
  // The DIY half of this page: the prompt box that opens the create surface and
  // the weekly suggestions it would author. Everything else here reports on
  // emails that already exist, so it stays either way.
  const selfServeEnabled = useFeatureFlag(FEATURE_FLAGS.SELF_SERVE_BUILDER);
  // A brand on the plugin does not author emails here, so the page should not
  // open by telling it that it does. Everything below the header already
  // adjusts; the sentence under the greeting was the last thing still
  // promising a product this brand did not buy.
  const canAuthor = useCanAuthorEmails();

  const [promptValue, setPromptValue] = useState('');
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showUpgrade, setShowUpgrade] = useState(false);
  // The same modal serves the weekly-suggestions CTA and the over-quota create
  // box; the type only changes the copy.
  const [upgradeType, setUpgradeType] = useState<UpgradeModalType>('suggestions');
  const [attachMenuAnchor, setAttachMenuAnchor] = useState<HTMLElement | null>(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<ProductVariantSearchResultDto[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<MediaAssetDto[]>([]);

  // (generation happens on create page via search params)

  // Personalized prompt suggestions for placeholder
  const { suggestions: promptSuggestions } = usePromptSuggestions();

  // Homepage data
  const { data, isLoading } = useHomepageControllerGetHomepageData(
    { orgId: orgId!, clientId: clientId! },
    {
      query: {
        enabled: !!clientId && !!orgId,
        staleTime: 60000,
        refetchOnWindowFocus: false,
      },
    }
  );

  // Billing quota
  const { data: quota } = useBillingControllerGetQuota(orgId!, undefined, {
    query: { enabled: !!orgId },
  });
  const isFreeTier = quota?.planSlug === 'free';
  // Undefined while the query is in flight — treat that as "not exceeded" so a
  // slow response never blocks a legitimate create. The server is the gate.
  const quotaExceeded = !!quota && !quota.canGenerate;

  // Weekly suggestions
  const { data: weeklyData, isLoading: weeklyLoading } =
    useTemplateSuggestionsControllerGetWeeklySuggestions(orgId!, clientId!, {
      query: {
        enabled: !!clientId && !!orgId && selfServeEnabled,
        refetchInterval: (query) => {
          const latest = query.state.data;
          if (latest?.status === 'generating') return 15000;
          // While we're in the learning window, recheck once an hour so the
          // dashboard can transition automatically when the delay expires.
          if (latest?.status === 'learning_brand') return 60 * 60 * 1000;
          const completedWithPreview =
            latest?.suggestions?.filter((s) => s.status === 'completed' && s.thumbnailUrl)?.length ?? 0;
          return completedWithPreview < 3 ? 15000 : false;
        },
        staleTime: 60000,
        refetchOnWindowFocus: false,
      },
    });

  // Campaign analytics for stats (memoized to prevent re-render loops)
  const { thisMonthStart, nowIso, lastMonthStart, lastMonthEnd } = useMemo(() => {
    const d = new Date();
    return {
      thisMonthStart: new Date(d.getFullYear(), d.getMonth(), 1).toISOString(),
      nowIso: d.toISOString(),
      lastMonthStart: new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString(),
      lastMonthEnd: new Date(d.getFullYear(), d.getMonth(), 0, 23, 59, 59).toISOString(),
    };
  }, []);

  const { data: campaignData } = useCampaignAnalyticsControllerGetCampaignOverview(
    {
      orgId: orgId!,
      clientId: clientId!,
      startDate: thisMonthStart,
      endDate: nowIso,
      previousStartDate: lastMonthStart,
      previousEndDate: lastMonthEnd,
    },
    {
      query: {
        enabled: !!clientId && !!orgId,
        staleTime: 300000,
        refetchOnWindowFocus: false,
      },
    }
  );

  const kpis = campaignData?.kpis;
  const firstName = dbUser?.firstName ?? '';
  const isNewUser = dbUser?.createdAt
    ? Date.now() - new Date(dbUser.createdAt).getTime() < 24 * 60 * 60 * 1000
    : false;

  const greeting = isNewUser
    ? t('home.greetingNew')
    : firstName
      ? t('home.greetingReturning', { name: firstName })
      : t('home.greetingReturningNoName');

  const handlePromptSubmit = () => {
    if (!orgId || !clientId) return;
    // Over quota: creating would 403 at the server anyway, so prompt to upgrade
    // here rather than handing off to a create page that can only fail.
    if (quotaExceeded) {
      setUpgradeType('generation');
      setShowUpgrade(true);
      return;
    }
    const tp = paths.dashboard.tenant(orgId, clientId);
    if (!promptValue.trim()) {
      navigate({ to: tp.emailCreate });
      return;
    }

    // Navigate to create page with all params; it auto-generates on mount
    navigate({
      to: tp.emailCreate,
      search: {
        prompt: promptValue.trim(),
        autoGenerate: true,
        productVariantIds: selectedProducts.length > 0
          ? selectedProducts.map((p) => p.variantId).join(',')
          : undefined,
        mediaAssetIds: selectedMedia.length > 0
          ? selectedMedia.map((m) => m.id).join(',')
          : undefined,
      },
    });
  };

  const hasPickerAttachments = selectedProducts.length > 0 || selectedMedia.length > 0;

  const completedSuggestions =
    weeklyData?.suggestions?.filter((s) => s.status === 'completed' && s.thumbnailUrl) ?? [];
  const totalSuggestions = completedSuggestions.length;

  const getWrappedIndex = (offset: number) => {
    if (totalSuggestions === 0) return 0;
    return ((carouselIndex + offset) % totalSuggestions + totalSuggestions) % totalSuggestions;
  };

  return (
    <DashboardContent maxWidth="xl" sx={{ pt: { xs: 2, sm: 4 } }}>
      {/* ===== Header ===== */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            {greeting}
          </Typography>
          <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>
            {canAuthor ? t('home.subtitle') : t('home.subtitleProduction')}
          </Typography>
        </Box>
      </Box>

      {/* ===== Notifications (setup, brand review, connect ESP) ===== */}
      <DashboardNotifications />

      {/* ===== Gradient CTA Banner ===== */}
      {selfServeEnabled && (
        <Box
          sx={{
            borderRadius: '8px',
            background: 'linear-gradient(90deg, #3CB4B8, #2A5ADF)',
            p: 2,
            mb: 2,
            boxShadow: '0 4px 24px 16px rgba(0,0,0,0.05)',
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <Box sx={{ flex: '0 0 auto' }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: 'white', mb: 0.5 }}>
              {t('home.cta.title')}
            </Typography>
            <Typography sx={{ fontSize: 14, color: alpha('#fff', 0.85) }}>
              {t('home.cta.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            {/* Attachment chips row */}
            {hasPickerAttachments && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                {selectedProducts.map((p) => (
                  <Chip
                    key={p.variantId}
                    icon={<ProductIcon sx={{ fontSize: 14 }} />}
                    label={p.productTitle}
                    onDelete={() => setSelectedProducts((prev) => prev.filter((x) => x.variantId !== p.variantId))}
                    deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                    size="small"
                    sx={{ bgcolor: alpha('#fff', 0.9), color: 'text.primary', fontSize: 12, height: 24, '& .MuiChip-icon': { color: 'text.secondary' }, '& .MuiChip-deleteIcon': { color: 'text.secondary' } }}
                  />
                ))}
                {selectedMedia.map((m) => (
                  <Chip
                    key={m.id}
                    icon={<AttachIcon sx={{ fontSize: 14 }} />}
                    label={m.title || m.filename}
                    onDelete={() => setSelectedMedia((prev) => prev.filter((x) => x.id !== m.id))}
                    deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                    size="small"
                    sx={{ bgcolor: alpha('#fff', 0.9), color: 'text.primary', fontSize: 12, height: 24, '& .MuiChip-icon': { color: 'text.secondary' }, '& .MuiChip-deleteIcon': { color: 'text.secondary' } }}
                  />
                ))}
              </Stack>
            )}

            {/* Input row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'white',
                borderRadius: '12px',
                pl: 2.5,
                pr: 0.75,
                py: 0.75,
              }}
            >
              <IconButton
                onClick={(e) => setAttachMenuAnchor(e.currentTarget)}
                title={t('home.cta.attachTitle')}
                sx={{
                  width: 34,
                  height: 34,
                  mr: 0.5,
                  color: hasPickerAttachments ? 'primary.main' : 'grey.500',
                  border: '1px solid',
                  borderColor: hasPickerAttachments ? 'primary.main' : 'grey.300',
                  '&:hover': { bgcolor: alpha('#000', 0.04), borderColor: 'grey.400' },
                }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Menu
                anchorEl={attachMenuAnchor}
                open={!!attachMenuAnchor}
                onClose={() => setAttachMenuAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 180, mt: -1 } } }}
              >
                <MenuItem onClick={() => { setAttachMenuAnchor(null); setShowProductPicker(true); }}>
                  <ListItemIcon><ProductIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('home.cta.products')}</ListItemText>
                  {selectedProducts.length > 0 && <Chip label={selectedProducts.length} size="small" color="primary" sx={{ height: 18, fontSize: 11, ml: 1 }} />}
                </MenuItem>
                <MenuItem onClick={() => { setAttachMenuAnchor(null); setShowMediaPicker(true); }}>
                  <ListItemIcon><AttachIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('home.cta.media')}</ListItemText>
                  {selectedMedia.length > 0 && <Chip label={selectedMedia.length} size="small" color="primary" sx={{ height: 18, fontSize: 11, ml: 1 }} />}
                </MenuItem>
              </Menu>
              <InputBase
                placeholder={
                  promptSuggestions[0]?.headline || promptSuggestions[0]?.text
                    ? t('home.cta.placeholderTry', {
                        example: (promptSuggestions[0].headline || promptSuggestions[0].text).slice(0, 40),
                      })
                    : t('home.cta.placeholderDefault')
                }
                value={promptValue}
                onChange={(e) => setPromptValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handlePromptSubmit()}
                autoComplete="off"
                inputProps={{ 'data-1p-ignore': true, 'data-lpignore': true }}
                sx={{
                  flex: 1,
                  fontSize: 14,
                  color: 'text.primary',
                  '& input::placeholder': { color: 'text.secondary', opacity: 0.7 },
                }}
              />
              <IconButton
                onClick={handlePromptSubmit}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* ===== Stats Row ===== */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 2,
        }}
      >
        <StatCard
          icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          label={t('home.stats.emailsCreated')}
          value={data?.emailsCreatedThisMonth?.current ?? 0}
          trend={data?.emailsCreatedThisMonth?.trend ?? 'neutral'}
          change={data?.emailsCreatedThisMonth?.percentChange ?? 0}
          loading={isLoading}
        />
        <StatCard
          icon={<SendIcon sx={{ fontSize: 16 }} />}
          label={t('home.stats.totalSends')}
          value={kpis?.totalDeliveries?.value ?? 0}
          trend={kpis?.totalDeliveries?.trend ?? 'neutral'}
          change={kpis?.totalDeliveries?.percentChange ?? 0}
          loading={isLoading}
          format="number"
        />
        <StatCard
          icon={<MailOutlineIcon sx={{ fontSize: 16 }} />}
          label={t('home.stats.avgOpenRate')}
          value={kpis?.avgOpenRate?.value ?? 0}
          trend={kpis?.avgOpenRate?.trend ?? 'neutral'}
          change={kpis?.avgOpenRate?.percentChange ?? 0}
          loading={isLoading}
          format="percent"
        />
      </Box>

      {/* ===== Main Layout ===== */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: selfServeEnabled ? { xs: '1fr', md: '7fr 5fr' } : '1fr',
          gap: 2,
          alignItems: 'start',
        }}
      >
        {/* Left Column: Recent + Tip */}
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'grey.200',
              p: 3,
              overflow: 'hidden',
            }}
          >
          {/* Recent Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'text.primary' }}>
              {t('home.recent.title')}
            </Typography>
            {data?.recentEmails?.length ? (
              <Typography
                onClick={() => navigate({ to: paths.dashboard.templates })}
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'primary.main',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {t('home.recent.viewAll')} <ChevronRightIcon sx={{ fontSize: 16 }} />
              </Typography>
            ) : null}
          </Box>

          {/* Recent Email List */}
          {isLoading ? (
            <Stack spacing={1}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height={72} sx={{ borderRadius: '10px' }} />
              ))}
            </Stack>
          ) : data?.recentEmails?.length ? (
            <Stack spacing={0.5}>
              {data.recentEmails.slice(0, 4).map((email) => {
                const statusCfg = STATUS_CONFIG[email.status] ?? STATUS_CONFIG.draft;
                return (
                  <Box
                    key={email.id}
                    onClick={() =>
                      navigate({
                        to:
                          email.renderMode === 'chat'
                            ? `/dashboard/emails/${email.id}/chat`
                            : paths.dashboard.emailEdit(email.id),
                      })
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.50' },
                    }}
                  >
                    {/* Thumbnail */}
                    <Box
                      sx={{
                        width: 48,
                        height: 64,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        bgcolor: 'grey.100',
                        flexShrink: 0,
                        position: 'relative',
                      }}
                    >
                      {email.thumbnailUrl ? (
                        <img
                          src={email.thumbnailUrl}
                          alt={email.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'top',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <MailOutlineIcon sx={{ fontSize: 20, color: 'grey.400' }} />
                        </Box>
                      )}
                    </Box>

                    {/* Title + Time */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {email.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {t('home.recent.edited', { time: formatRelative(email.createdAt) })}
                      </Typography>
                    </Box>

                    {/* A/B variant chip — surfaces sibling count for grouped
                        primaries. Siblings are filtered server-side so this
                        only appears on the group primary; count includes the
                        primary itself. Mirrors templates list TemplateCard. */}
                    {email.variantGroupId && (email.variantCount ?? 0) > 1 && (
                      <Chip
                        label={t('home.recent.abVariant', { count: email.variantCount })}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          height: 24,
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          flexShrink: 0,
                        }}
                      />
                    )}

                    {/* Status Chip */}
                    <Chip
                      label={t(`home.status.${email.status}`, statusCfg.label)}
                      size="small"
                      sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        height: 24,
                        bgcolor: statusCfg.bgcolor,
                        color: statusCfg.color,
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                bgcolor: 'grey.50',
                borderRadius: '12px',
              }}
            >
              <EditNoteIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                {t('home.recent.empty')}
              </Typography>
            </Box>
          )}

          {/* Tip Bar */}
          {data?.tipOfTheDay && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 2,
                p: 2,
                borderRadius: '10px',
                bgcolor: 'grey.50',
              }}
            >
              <LightbulbOutlinedIcon sx={{ fontSize: 20, color: 'warning.main', flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: 13,
                  color: 'text.secondary',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {data.tipOfTheDay.content}
              </Typography>
            </Box>
          )}
          </Box>
        </Box>

        {/* Right Column: Suggested Emails Carousel */}
        {selfServeEnabled && (
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'text.primary' }}>
                {t('home.suggested.title')}
              </Typography>
            </Box>

            {/* Center-focused Carousel: always 3 visible slots */}
            {weeklyData?.status === 'learning_brand' ? (
              <LearningBrandCard
                onCompleteBrand={
                  clientId
                    ? () =>
                        navigate({ to: paths.dashboard.orgSettings(orgId ?? '').client.brandDetail(clientId) })
                    : undefined
                }
              />
            ) : weeklyLoading || (weeklyData?.status === 'generating' && totalSuggestions === 0) ? (
              <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5 }}>
                {[0, 1, 2].map((slot) => {
                  const isSide = slot !== 1;
                  return (
                    <Card
                      key={slot}
                      sx={{
                        flex: isSide ? '0 0 8%' : '1 1 auto',
                        opacity: isSide ? 0.35 : 1,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        sx={{ paddingTop: '85%', width: '100%' }}
                      />
                      {!isSide && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                          }}
                        >
                          <AutoAwesomeIcon
                            sx={{
                              fontSize: 28,
                              color: 'primary.main',
                              '@keyframes pulse-icon': {
                                '0%, 100%': { opacity: 0.4, transform: 'scale(1)' },
                                '50%': { opacity: 0.8, transform: 'scale(1.1)' },
                              },
                              animation: 'pulse-icon 2s ease-in-out infinite',
                            }}
                          />
                          <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
                            {t('home.suggested.crafting')}
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  );
                })}
              </Box>
            ) : totalSuggestions > 0 ? (
              <>
                {/* 3-slot layout: arrow | prev peek | center | next peek | arrow */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, maxWidth: '100%' }}>
                  <IconButton
                    size="small"
                    disabled={totalSuggestions === 0}
                    onClick={() => setCarouselIndex((i) => (i - 1 + totalSuggestions) % totalSuggestions)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.300',
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  {[-1, 0, 1].map((offset) => {
                    const idx = getWrappedIndex(offset);
                    const item = completedSuggestions[idx];
                    const isCenter = offset === 0;
                    return (
                      <Box
                        key={`${offset}-${idx}`}
                        sx={{
                          flex: isCenter ? '1 1 60%' : '0 0 15%',
                          minWidth: 0,
                          opacity: isCenter ? 1 : 0.3,
                          transition: 'opacity 0.3s ease',
                          cursor: isCenter ? undefined : 'pointer',
                          overflow: 'hidden',
                          borderRadius: isCenter ? '12px' : 0,
                        }}
                        onClick={
                          !isCenter
                            ? () => setCarouselIndex(idx)
                            : undefined
                        }
                      >
                        <Card
                          sx={{
                            borderRadius: isCenter ? '12px' : 0,
                            border: isCenter ? '1px solid' : 'none',
                            borderColor: 'grey.200',
                            boxShadow: 'none',
                            overflow: 'hidden',
                            height: '100%',
                            bgcolor: isCenter ? 'background.paper' : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': isCenter && item.templateId
                              ? {
                                  borderColor: 'primary.main',
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                }
                              : {},
                          }}
                        >
                          <CardActionArea
                            onClick={
                              isCenter && item.templateId
                                ? () => navigate({ to: paths.dashboard.emailEdit(item.templateId!) })
                                : undefined
                            }
                            disabled={!isCenter}
                            sx={{ display: 'block', height: '100%' }}
                          >
                            <Box
                              sx={{
                                position: 'relative',
                                width: '100%',
                                height: isCenter ? 480 : 480,
                                overflow: 'hidden',
                                bgcolor: 'grey.50',
                              }}
                            >
                              {item.thumbnailUrl ? (
                                <img
                                  src={item.thumbnailUrl}
                                  alt={item.templateName ?? t('home.suggested.emailPreviewAlt')}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'top',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <MailOutlineIcon sx={{ fontSize: isCenter ? 48 : 20, color: 'grey.300' }} />
                                </Box>
                              )}
                            </Box>
                          </CardActionArea>
                        </Card>
                      </Box>
                    );
                  })}
                  <IconButton
                    size="small"
                    disabled={totalSuggestions === 0}
                    onClick={() => setCarouselIndex((i) => (i + 1) % (totalSuggestions || 1))}
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.300',
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                    }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
                {/* Title + subject below */}
                <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'text.primary',
                      mb: 0.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {completedSuggestions[carouselIndex]?.templateName ?? t('home.suggested.untitled')}
                  </Typography>
                  {completedSuggestions[carouselIndex]?.subjectLine && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {completedSuggestions[carouselIndex].subjectLine}
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  px: 3,
                  bgcolor: 'grey.50',
                  borderRadius: '12px',
                }}
              >
                {weeklyData?.status === 'needs_setup' ? (
                  <>
                    <AutoAwesomeIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1.5 }} />
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                      {t('home.suggested.needsSetupTitle')}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5, maxWidth: 320, mx: 'auto' }}>
                      {t('home.suggested.needsSetupBody')}
                    </Typography>
                    {clientId && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => navigate({ to: paths.dashboard.orgSettings(orgId ?? '').client.brandDetail(clientId) })}
                        sx={{ textTransform: 'none', fontSize: 13 }}
                      >
                        {t('home.suggested.needsSetupCta')}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <MailOutlineIcon sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                      {t('home.suggested.empty')}
                    </Typography>
                  </>
                )}
              </Box>
            )}

            {/* Free-tier upgrade prompt */}
            {isFreeTier && totalSuggestions > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('home.suggested.upgradePrompt')}
                </Typography>
                <button
                  onClick={() => {
                    setUpgradeType('suggestions');
                    setShowUpgrade(true);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: theme.palette.primary.main,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textDecoration: 'underline',
                    padding: 0,
                  }}
                >
                  {t('home.suggested.viewPlans')}
                </button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Upgrade modal */}
      {orgId && (
        <UpgradeModal
          open={showUpgrade}
          onClose={() => setShowUpgrade(false)}
          type={upgradeType}
          organizationId={orgId}
          used={quota?.used ?? 0}
          limit={quota?.limit ?? 0}
          nextPlanName={quota?.nextPlanName}
        />
      )}

      {/* Picker modals for the create input */}
      <ProductPickerModal
        open={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        onSelect={setSelectedProducts}
        selectedProducts={selectedProducts}
      />
      <MediaPickerModal
        open={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={setSelectedMedia}
        selectedAssets={selectedMedia}
      />
    </DashboardContent>
  );
}

// ----------------------------------------------------------------------

function StatCard({
  icon,
  label,
  value,
  trend,
  change,
  loading,
  format,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  trend: string;
  change: number;
  loading: boolean;
  format?: 'number' | 'percent';
}) {
  const { t } = useTranslation('dashboard');
  const trendIcon =
    trend === 'up' ? (
      <TrendingUpIcon sx={{ fontSize: 14 }} />
    ) : trend === 'down' ? (
      <TrendingDownIcon sx={{ fontSize: 14 }} />
    ) : (
      <TrendingFlatIcon sx={{ fontSize: 14 }} />
    );

  const trendColor =
    trend === 'up' ? 'primary.main' : trend === 'down' ? 'error.main' : 'text.secondary';

  const trendLabel =
    trend === 'neutral'
      ? t('home.stats.noChange')
      : t('home.stats.vsLastMonth', { change: `${change > 0 ? '+' : ''}${change}` });

  const formattedValue =
    format === 'percent'
      ? `${value.toFixed(1)}%`
      : format === 'number'
        ? value.toLocaleString()
        : String(value);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'grey.200',
        p: 2.5,
      }}
    >
      {loading ? (
        <>
          <Skeleton width={100} height={16} sx={{ mb: 1 }} />
          <Skeleton width={60} height={36} sx={{ mb: 1 }} />
          <Skeleton width={120} height={14} />
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>
              {label}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 28, fontWeight: 700, color: 'text.primary', mb: 0.75 }}>
            {formattedValue}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: trendColor }}>
            {trendIcon}
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{trendLabel}</Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
